/**
 * What was sold, item by item.
 *
 * The phone works this out in `OrdersCubit.getOrdersSummary`, and so does
 * this — deliberately. There is no sales-summary endpoint: the server's report
 * routes stop at the income statement and a per-category rollup, so both
 * clients derive the same figures from the same orders list. Copying its
 * derivation exactly is what keeps the two from disagreeing about how many
 * Sambuus went out today.
 *
 * ── The two totals, and why they differ ─────────────────────────────────────
 *
 * `sales` is the sum of the ITEM lines — `items[].subtotal`, which is quantity
 * times price and knows nothing about a discount given on the whole order.
 * `net` is the sum of `order.total`, which is what the shop actually took.
 *
 * So the item lines add up to MORE than the takings whenever anybody gave a
 * discount, and that gap is not an error — it is the discount. The screen and
 * the PDF both show it rather than quietly making one number match the other,
 * which is the phone's own reconciliation footer.
 *
 * Cancelled orders never appear: the orders endpoint filters them out before
 * they are sent.
 */

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Rounded at every step, so a long day cannot drift by a cent. */
const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

export const SORT = { QUANTITY: 'quantity', REVENUE: 'revenue' };

/**
 * One row per menu item, plus the totals the header and footer need.
 *
 * Grouped by NAME, not by id, exactly as the phone groups it: two items with
 * the same name merge into one line, and an item renamed halfway through the
 * period shows as two. That is a real limitation, kept on purpose — a desktop
 * that grouped by id would show a different table from the phone for the same
 * week, and the shop would have no way to tell which was right.
 *
 * @param {Array} orders orders as the API sends them
 */
export function summariseSales(orders) {
  const byName = new Map();

  let lineSum = 0;
  let unitsSold = 0;
  let net = 0;
  let discount = 0;

  (orders || []).forEach((order) => {
    net += toNumber(order?.total);
    discount += toNumber(order?.discount);

    const items = Array.isArray(order?.items) ? order.items : [];

    items.forEach((item) => {
      const name = String(item?.name || '').trim() || '—';
      const quantity = toNumber(item?.quantity);
      // `subtotal` is stored on every line, but an old order written before it
      // was defaulted has none — quantity times price is the same number.
      const sales =
        item?.subtotal === undefined || item?.subtotal === null
          ? quantity * toNumber(item?.price)
          : toNumber(item.subtotal);

      const row = byName.get(name) || { name, quantity: 0, sales: 0, lines: 0 };
      row.quantity += quantity;
      row.sales += sales;
      row.lines += 1;
      byName.set(name, row);

      lineSum += sales;
      unitsSold += quantity;
    });
  });

  const rows = Array.from(byName.values()).map((row) => ({
    ...row,
    sales: round2(row.sales),
    quantity: round2(row.quantity),
  }));

  return {
    rows,
    orderCount: (orders || []).length,
    productCount: rows.length,
    unitsSold: round2(unitsSold),
    /** The item lines added up — gross of any order-level discount. */
    sales: round2(lineSum),
    /** What the shop actually took. */
    net: round2(net),
    /** The discount recorded on the orders themselves. */
    discount: round2(discount),
    /**
     * The gap between the lines and the takings. Normally the same as
     * `discount`; it is computed separately because it is the figure that
     * makes the two columns on screen reconcile, whatever caused it.
     */
    gap: round2(lineSum - net),
  };
}

/** Biggest first, by whichever measure is being read. A tie falls back to name. */
export function sortRows(rows, sort) {
  const sorted = rows.slice();

  sorted.sort((a, b) => {
    const left = sort === SORT.REVENUE ? b.sales - a.sales : b.quantity - a.quantity;
    if (left !== 0) return left;
    const second = sort === SORT.REVENUE ? b.quantity - a.quantity : b.sales - a.sales;
    if (second !== 0) return second;
    return String(a.name).localeCompare(String(b.name));
  });

  return sorted;
}

/**
 * A row's share of the total, measured the same way the table is sorted.
 *
 * Sorting by revenue and showing a share of units would put the biggest bar
 * against a row that is not at the top, which reads as a bug.
 */
export function shareOf(row, summary, sort) {
  const whole = sort === SORT.REVENUE ? summary.sales : summary.unitsSold;
  if (!whole) return 0;
  return ((sort === SORT.REVENUE ? row.sales : row.quantity) / whole) * 100;
}

/** Only worth showing when the two columns actually disagree. */
export const needsReconciliation = (summary) => Math.abs(summary.gap) > 0.005;

/** Free-text match on the item name. */
export function filterRows(rows, query) {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((row) => row.name.toLowerCase().indexOf(needle) !== -1);
}
